import { useCallback, useEffect, useMemo, useState } from 'react'
import { useRoute, type Route } from './lib/router.ts'
import { loadCourse, type CourseIndex } from './lib/course.ts'
import {
  DEFAULT_SETTINGS, StoreContext, applyGrade, buildSession, computeStreak,
  loadState, logReview, persistCard, persistDaily, persistSettings,
  type DayStats, type Settings, type Store,
} from './lib/store.ts'
import type { Card, Grade } from './lib/srs.ts'
import { dayKey } from './lib/time.ts'
import { cards as cardsDb, kv, log } from './lib/db.ts'
import { Loading } from './components/ui.tsx'
import { Home } from './screens/Home.tsx'
import { SessionScreen } from './screens/Session.tsx'
import { Modules, ModuleView } from './screens/Modules.tsx'
import { DialogueView } from './screens/DialogueView.tsx'
import { RoleplayView } from './screens/RoleplayView.tsx'
import { InsightView } from './screens/InsightView.tsx'
import { PlanScreen } from './screens/Plan.tsx'
import { StatsScreen } from './screens/Stats.tsx'
import { SettingsScreen } from './screens/Settings.tsx'
import { ExamScreen } from './screens/Exam.tsx'
import { ChatScreen } from './screens/Chat.tsx'
import { PairsDrill, PairsList } from './screens/Pairs.tsx'

export function App() {
  const route = useRoute()
  const [idx, setIdx] = useState<CourseIndex | null>(null)
  const [cards, setCards] = useState<Map<string, Card>>(new Map())
  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS)
  const [daily, setDaily] = useState<Record<string, DayStats>>({})
  const [now, setNow] = useState(() => Date.now())
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    loadCourse()
      .then(async (loaded) => {
        const state = await loadState(loaded)
        if (!alive) return
        setIdx(loaded)
        setCards(state.cards)
        setSettings(state.settings)
        setDaily(state.daily)
      })
      .catch((e: Error) => alive && setError(e.message))
    return () => { alive = false }
  }, [])

  // Час рухається — картки «дозрівають» просто під час заняття.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 20_000)
    return () => clearInterval(t)
  }, [])

  const grade = useCallback(
    (phraseId: string, g: Grade, ms: number) => {
      const at = Date.now()
      setCards((prev) => {
        const before = prev.get(phraseId)
        const after = applyGrade(before, phraseId, g, settings, at)
        void persistCard(after)
        const next = new Map(prev)
        next.set(phraseId, after)
        return next
      })
      setDaily((prev) => {
        const key = dayKey(at)
        const day = prev[key] ?? { introduced: 0, reviewed: 0, seconds: 0 }
        const wasNew = !cards.get(phraseId) || cards.get(phraseId)!.state === 'new'
        const next = {
          ...prev,
          [key]: {
            introduced: day.introduced + (wasNew ? 1 : 0),
            reviewed: day.reviewed + (wasNew ? 0 : 1),
            seconds: day.seconds + Math.min(60, Math.round(ms / 1000)),
          },
        }
        void persistDaily(next)
        return next
      })
      void logReview(phraseId, g, ms, at)
      setNow(at)
    },
    [cards, settings],
  )

  const update = useCallback((patch: Partial<Settings>) => {
    setSettings((prev) => {
      const next = { ...prev, ...patch }
      void persistSettings(next)
      return next
    })
  }, [])

  const reset = useCallback(async () => {
    await cardsDb.clear()
    await log.clear()
    await kv.set('daily', {})
    setCards(new Map())
    setDaily({})
  }, [])

  const session = useMemo(
    () => (idx ? buildSession(idx, cards, settings, daily, now) : null),
    [idx, cards, settings, daily, now],
  )

  if (error) {
    return (
      <div className="screen">
        <div className="card">
          <h2>Не вдалося завантажити курс</h2>
          <p className="muted small" style={{ marginTop: 8 }}>{error}</p>
          <button className="btn btn--wide" style={{ marginTop: 16 }} onClick={() => location.reload()}>
            Спробувати ще раз
          </button>
        </div>
      </div>
    )
  }

  if (!idx || !session) return <Loading />

  const store: Store = {
    idx, cards, settings, daily, session, now,
    streak: computeStreak(daily, now),
    grade, update, reset,
    refresh: () => setNow(Date.now()),
  }

  return (
    <StoreContext.Provider value={store}>
      <div className={`app${route.name === 'session' || (route.name === 'pairs' && route.id) ? ' app--focus' : ''}`}>
        {render(route)}
        <Nav route={route} />
      </div>
    </StoreContext.Provider>
  )
}

function render(route: Route) {
  switch (route.name) {
    case 'home': return <Home />
    case 'session': return <SessionScreen mode={route.mode} moduleId={route.moduleId} />
    case 'modules': return <Modules />
    case 'module': return <ModuleView id={route.id} />
    case 'dialogue': return <DialogueView moduleId={route.moduleId} id={route.id} />
    case 'roleplay': return <RoleplayView moduleId={route.moduleId} id={route.id} />
    case 'insight': return <InsightView moduleId={route.moduleId} id={route.id} />
    case 'plan': return <PlanScreen />
    case 'stats': return <StatsScreen />
    case 'settings': return <SettingsScreen />
    case 'exam': return <ExamScreen />
    case 'chat': return <ChatScreen moduleId={route.moduleId} />
    case 'pairs': return route.id ? <PairsDrill id={route.id} /> : <PairsList />
  }
}

const TABS = [
  { href: '#/', icon: '🏠', label: 'Сьогодні', match: ['home', 'session'] },
  { href: '#/modules', icon: '📚', label: 'Модулі', match: ['modules', 'module', 'dialogue', 'roleplay', 'insight', 'pairs'] },
  { href: '#/plan', icon: '🗓', label: 'План', match: ['plan', 'exam'] },
  { href: '#/stats', icon: '📈', label: 'Прогрес', match: ['stats', 'settings'] },
] as const

function Nav({ route }: { route: Route }) {
  return (
    <nav className="nav">
      {TABS.map((tab) => (
        <a
          key={tab.href}
          href={tab.href}
          aria-current={(tab.match as readonly string[]).includes(route.name) ? 'page' : undefined}
        >
          <span className="nav__icon" aria-hidden>{tab.icon}</span>
          {tab.label}
        </a>
      ))}
    </nav>
  )
}
